import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { ChevronDown, Download, File, Trash } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import type { CharterAttachmentPayload } from '@ankor-io/common/types/charter'
import { formatDate } from '@ankor-io/common/util/dates'

import { ALLOWED_FILE_TYPES } from '@/constants/fileTypes'
import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Heading } from '@/ui-components/Heading'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui-components/Table'
import { FileUploadZone } from '@/views/components/FileUploadZone'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes

type FilesAndDocumentsProps = {
  transactionId: string
  canDeleteAttachments: boolean
}

const FilesAndDocuments = ({ transactionId, canDeleteAttachments }: FilesAndDocumentsProps) => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()
  const [attachments, setAttachments] = useState<CharterAttachmentPayload[]>([])
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [attachmentsToDelete, setAttachmentsToDelete] = useState<string[]>([])

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transactionId}/attachments`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
          },
        )

        if (!res.ok) {
          throw new Error('Failed to fetch files')
        }

        const data = await res.json()
        setAttachments(data.attachments)
      } catch (error) {
        console.error('Error fetching files:', error)
      }
    }

    fetchFiles()
  }, [activeConnectedAccountId, activeOrganizationId, transactionId, getAccessToken])

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return

    setIsUploading(true)
    let failedUploadCount = 0

    try {
      const uploadPromises = selectedFiles.map(async (selectedFile: File) => {
        // 1. get the pre-signed url
        const preSignedUrlRes = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transactionId}/attachment/upload`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            body: JSON.stringify({ path: selectedFile.name }),
          },
        )

        if (!preSignedUrlRes.ok) {
          console.error('Failed to get pre-signed URL', preSignedUrlRes)
          failedUploadCount++
          throw new Error('Failed to get pre-signed URL')
        }

        const preSignedUrl = (await preSignedUrlRes.json()) as {
          url: string
          path: string
        }

        // 2. upload the file to the pre-signed url
        const uploadRes = await fetch(preSignedUrl.url, {
          method: 'PUT',
          body: selectedFile,
        })

        if (!uploadRes.ok) {
          console.error('Failed to upload file', uploadRes)
          failedUploadCount++
          throw new Error('Failed to upload file')
        }

        // 3. create the attachment
        const createAttachmentRes = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transactionId}/attachments`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            body: JSON.stringify({
              name: selectedFile.name,
              mimeType: selectedFile.type,
            }),
          },
        )

        if (!createAttachmentRes.ok) {
          console.error('Failed to create attachment', createAttachmentRes)
          failedUploadCount++
          throw new Error('Failed to create attachment')
        }
      })

      await Promise.allSettled(uploadPromises)

      setSelectedFiles([])

      if (failedUploadCount === selectedFiles.length) {
        toast.error('Upload failed')
      } else if (failedUploadCount > 0) {
        toast.error(`Failed to upload ${failedUploadCount} file${failedUploadCount > 1 ? 's' : ''}`)
      } else {
        toast.success(`File${selectedFiles.length > 1 ? 's' : ''} uploaded successfully`)
      }

      // 4. refresh the attachments list
      const res = await fetch(
        `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transactionId}/attachments`,
        {
          headers: {
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        },
      )

      const data = await res.json()
      setAttachments(data.attachments)
    } catch (error) {
      console.error('Error uploading files:', error)
    } finally {
      setIsUploading(false)
    }
  }

  const getFileSize = (file: File): string => {
    const BYTES_PER_KB = 1024
    const BYTES_PER_MB = BYTES_PER_KB * 1024

    const fileSizeInBytes = file.size

    if (fileSizeInBytes < BYTES_PER_MB) {
      const sizeInKb = fileSizeInBytes / BYTES_PER_KB
      return `${sizeInKb.toFixed(2)} KB`
    }

    const sizeInMb = fileSizeInBytes / BYTES_PER_MB
    return `${sizeInMb.toFixed(2)} MB`
  }

  const deleteAttachment = async (attachmentId: string) => {
    setAttachmentsToDelete((prev) => [...prev, attachmentId])
    try {
      const res = await fetch(
        `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transactionId}/attachments/${attachmentId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        },
      )

      if (!res.ok) {
        throw new Error('Failed to refresh attachments list')
      }

      // update the attachments list. No need to refresh the list via fetch.
      setAttachments((prev) => prev.filter((attachment) => attachment.id !== attachmentId))
    } catch (error) {
      console.error('Error deleting attachment:', error)
    } finally {
      setAttachmentsToDelete((prev) => prev.filter((id) => id !== attachmentId))
    }
  }

  const downloadAttachment = async (attachmentId: string) => {
    try {
      const res = await fetch(
        `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transaction/${transactionId}/attachments/${attachmentId}`,
        {
          headers: {
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        },
      )

      if (!res.ok) {
        throw new Error('Failed to download attachment')
      }

      const data = (await res.json()) as { attachment: CharterAttachmentPayload }

      // Download the file content from the signed URL
      const blobResponse = await fetch(data.attachment.url)
      if (!blobResponse.ok) {
        throw new Error('Failed to fetch file content')
      }

      const blob = await blobResponse.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = data.attachment.name || 'download' // Set filename for download
      a.style.display = 'none' // Hide the link
      document.body.appendChild(a) // Append to DOM
      a.click() // Trigger download
      document.body.removeChild(a) // Clean up DOM
      window.URL.revokeObjectURL(url) // Clean up blob URL
    } catch (error) {
      console.error('Error downloading attachment:', error)
    }
  }

  return (
    <div className="flex flex-col gap-y-4 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex md:items-center justify-between flex-col md:flex-row gap-4">
        <Heading level={3}>Files and Documents</Heading>
        <Button
          color="primary"
          onClick={() => setIsUploadOpen(!isUploadOpen)}
        >
          <ChevronDown
            className={`${isUploadOpen ? 'rotate-180' : ''} transition-transform duration-300`}
            data-slot="icon"
            data-testid="chevron-down"
          />
          Add File
        </Button>
      </div>

      {/* Upload Accordion */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isUploadOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
        data-testid="upload-accordion"
      >
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-800/50">
          <FileUploadZone
            selectedFiles={selectedFiles}
            onFilesChange={setSelectedFiles}
            onUpload={uploadFiles}
            isUploading={isUploading}
            allowedFileTypes={ALLOWED_FILE_TYPES}
            maxFileSize={MAX_FILE_SIZE}
            getFileSize={getFileSize}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table className="max-w-[calc(100vw-7rem)] lg:max-w-[calc(100vw-24rem)]">
          <TableHead>
            <TableRow>
              <TableHeader>Filename</TableHeader>
              <TableHeader>Uploaded Date</TableHeader>
              <TableHeader></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {attachments?.map((attachment) => (
              <TableRow key={attachment.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <File />
                    <span className="text-sm text-gray-500 dark:text-gray-400">{attachment.name}</span>
                  </div>
                </TableCell>
                <TableCell>{formatDate(attachment.uploadedAt)}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-around gap-2">
                    <Button
                      plain
                      onClick={() => downloadAttachment(attachment.id)}
                      data-testid={`download-button-${attachment.id}`}
                    >
                      <Download data-slot="icon" />
                    </Button>
                    {canDeleteAttachments && (
                      <Button
                        className="disabled:opacity-50 disabled:cursor-not-allowed"
                        plain
                        onClick={() => deleteAttachment(attachment.id)}
                        disabled={attachmentsToDelete.includes(attachment.id)}
                        data-testid={`delete-button-${attachment.id}`}
                      >
                        <Trash data-slot="icon" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {attachments.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-sm text-gray-500 dark:text-gray-400">No files and documents found</div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FilesAndDocuments
